import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useState } from "react";
import { DilemmasHttpRepository } from "../repository";
import { useDilemmasFilterStore } from "../stores";
import { useDebounce } from "@/hooks/useDebounce";
import { DILEMMAS_STATUS } from "@/features/dilemmas/constants";

const PAGE_SIZE = 10;
const dilemmasRepository = new DilemmasHttpRepository();

export function useDilemmasList() {
  const [page, setPage] = useState(1);
  const searchValue = useDilemmasFilterStore((s) => s.searchValue);
  const statusFilter = useDilemmasFilterStore((s) => s.statusFilter);
  const ordering = useDilemmasFilterStore((s) => s.ordering);
  const languageFilter = useDilemmasFilterStore((s) => s.languageFilter);
  const themeFilter = useDilemmasFilterStore((s) => s.themeFilter);
  const debouncedSearch = useDebounce(searchValue, 500);

  const [prevSearch, setPrevSearch] = useState(debouncedSearch);
  if (debouncedSearch !== prevSearch) {
    setPrevSearch(debouncedSearch);
    setPage(1);
  }

  const [prevStatusFilter, setPrevStatusFilter] = useState(statusFilter);
  if (statusFilter !== prevStatusFilter) {
    setPrevStatusFilter(statusFilter);
    setPage(1);
  }

  const [prevLanguageFilter, setPrevLanguageFilter] = useState(languageFilter);
  if (languageFilter !== prevLanguageFilter) {
    setPrevLanguageFilter(languageFilter);
    setPage(1);
  }

  const [prevOrdering, setPrevOrdering] = useState(ordering);
  if (ordering !== prevOrdering) {
    setPrevOrdering(ordering);
    setPage(1);
  }

  const [prevThemeFilter, setPrevThemeFilter] = useState(themeFilter);
  if (themeFilter !== prevThemeFilter) {
    setPrevThemeFilter(themeFilter);
    setPage(1);
  }

  const query = useQuery({
    queryKey: [
      "dilemmas",
      {
        page,
        search: debouncedSearch,
        status: statusFilter,
        source_language: languageFilter,
        ordering,
        topic: themeFilter,
      },
    ],
    queryFn: () =>
      dilemmasRepository.fetchList({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
        search: debouncedSearch || undefined,
        status: statusFilter ?? undefined,
        source_language: languageFilter ?? undefined,
        source: "admin",
        ordering: ordering ?? undefined,
        topic: themeFilter ?? undefined,
      }),
    placeholderData: keepPreviousData,
  });

  const pageCount = query.data ? Math.ceil(query.data.count / PAGE_SIZE) : 0;

  const dilemmas = query.data?.results ?? [];

  return {
    dilemmas: dilemmas.map((d) => ({
      ...d,
      published_at:
        d.status === DILEMMAS_STATUS.SCHEDULED
          ? d.scheduled_for_date
          : d.published_at,
    })),
    pageCount,
    page,
    setPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isPlaceholderData: query.isPlaceholderData,
  };
}
